import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  useGetMeterConsumptionQuery,
  useGetMeterConsumptionGraphQuery,
} from '../../../../../service/vending-service';
import styles from './PostpaidEnergyProfileBox.module.scss';
import { ApplicationRoutes } from '../../../../../routes';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function mondayIndex(date: Date): number {
  const d = date.getDay(); // 0=Sun
  return d === 0 ? 6 : d - 1; // 0=Mon … 6=Sun
}

const NoDataBadge: React.FC = () => {
  const { t } = useTranslation();
  return <span className={styles.noData}>{t('common.labels.noData')}</span>;
};


export const PostpaidEnergyProfileBox: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { yesterdayParams, weekParams, todayIndex } = useMemo(() => {
    const now = new Date();
    const daysFromMonday = mondayIndex(now);
    const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
    return {
      yesterdayParams: { from: yesterdayStart.toISOString(), to: todayStart.toISOString(), isMonthly: false },
      weekParams: { from: weekStart.toISOString(), to: now.toISOString(), isMonthly: false },
      todayIndex: daysFromMonday,
    };
  }, []);

  const { data: yesterdayData, isLoading: yesterdayLoading } = useGetMeterConsumptionQuery(yesterdayParams);
  const { data: graphData, isLoading: graphLoading } = useGetMeterConsumptionGraphQuery(weekParams);

  const chartValues = useMemo(() => {
    const values = Array<number>(7).fill(0);
    if (!graphData?.data?.length) return values;
    const weekStart = new Date(weekParams.from);
    graphData.data.forEach(item => {
      const idx = Math.round((new Date(item.date).getTime() - weekStart.getTime()) / 86400000);
      if (idx >= 0 && idx < 7) values[idx] = item.consumption;
    });
    return values;
  }, [graphData, weekParams.from]);

  const hasChartData = chartValues.some(v => v > 0);

  const barColors = DAY_LABELS.map((_, i) =>
    i === todayIndex ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)'
  );

  const barData = {
    labels: DAY_LABELS,
    datasets: [{
      data: chartValues,
      backgroundColor: barColors,
      borderRadius: 2,
      maxBarThickness: 32,
    }],
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#F9A01B',
        titleColor: 'transparent',
        bodyColor: '#fff',
        displayColors: false,
        padding: 8,
        callbacks: {
          title: () => '',
          label: ctx => `${Number(ctx.parsed.y).toFixed(2)} kWh`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 11 } },
      },
      y: { display: false },
    },
  };

  const consumption = yesterdayData?.consumption;
  const formattedValue = consumption != null
    ? consumption.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  return (
    <div className={styles.card}>

      <div className={styles.header}>
        <div>
          <p className={styles.label}>{t('energyManagement.energyProfile.yesterdaysConsumptions')}</p>
          {yesterdayLoading ? (
            <span className={styles.shimmer} />
          ) : formattedValue ? (
            <p className={styles.value}>
              {formattedValue}<span className={styles.unit}>kWh</span>
            </p>
          ) : (
            <NoDataBadge />
          )}
        </div>

        <button className={styles.moreReportBtn} onClick={() => navigate(`/${ApplicationRoutes.CUSTOMER_ENERGY_MANAGEMENT}`)}>
          <span className={styles.arrowCircle}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M3 13L13 3M13 3H6M13 3V10" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {t('energyManagement.energyProfile.moreReport')}
        </button>
      </div>

      <p className={styles.trendLabel}>{t('energyManagement.thisWeeksConsumptionTrend')}</p>

      <div className={styles.chartWrap}>
        {!graphLoading && !hasChartData ? (
          <div className={styles.noDataChart}><NoDataBadge /></div>
        ) : (
          <Bar data={barData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};
